import { Icon } from '@common/components/Icon';
import { Palette } from '@common/theming/Palette';
import React from 'react';
import { DESKTOP_CONTENT_WIDTH } from '../../constants';
import { PortfolioAsset } from '../../types';
import { TextBlock } from '../TextBlock';
type HighlightsProps = {
  highlights: PortfolioAsset['assetHighlights'];
};
export const Highlights: React.FC<HighlightsProps> = ({ highlights }) => {
  let keys = Object.keys(highlights) as Array<keyof PortfolioAsset['assetHighlights']>;

  // If LTV or ARV is in keys, move it to the middle for display reasons, if both exist, remove LTV
  const ltvKey = keys.findIndex((key) => key === 'ltv');
  const ltarvKey = keys.findIndex((key) => key === 'ltarv');

  if (ltarvKey !== -1 || ltvKey !== -1) {
    const filteredKeys = keys.filter((key) => {
      return key !== 'ltv' && key !== 'ltarv';
    }) as Array<keyof PortfolioAsset['assetHighlights']>;
    if (filteredKeys.length > 0) {
      const keysNew = [filteredKeys[0]];
      keysNew.push(ltarvKey !== -1 ? 'ltarv' : 'ltv');
      if (filteredKeys.length > 1) {
        keysNew.push(filteredKeys[1]);
      }
      keys = keysNew;
    }
  }

  const renderValue = (key: keyof PortfolioAsset['assetHighlights'], textAlign: React.CSSProperties['textAlign']) => {
    switch (key) {
      case 'ltv':
        return (
          <div style={{ alignItems: 'center', display: 'flex' }}>
            <div>
              <div
                style={{
                  alignItems: 'center',
                  backgroundColor: Palette.WHITE,
                  borderRadius: 25,
                  display: 'flex',
                  height: 40,
                  justifyContent: 'center',
                  width: 40,
                }}>
                <Icon color="black" boxSize={'20px'} icon="chart-pie" />
              </div>
            </div>
            <div style={{ marginLeft: 18 }}>
              <TextBlock size={10} weight={'bold'} color={Palette.GRAY} style={{ textAlign }}>
                LTV
              </TextBlock>
              <TextBlock weight="bold" color={Palette.BLACK} style={{ textAlign }}>
                {highlights.ltv}
              </TextBlock>
            </div>
          </div>
        );
      case 'ltarv':
        return (
          <div style={{ alignItems: 'center', display: 'flex' }}>
            <div>
              <div
                style={{
                  alignItems: 'center',
                  backgroundColor: Palette.WHITE,
                  borderRadius: 25,
                  display: 'flex',
                  height: 40,
                  justifyContent: 'center',
                  width: 40,
                }}>
                <Icon color="black" boxSize={'20px'} icon="chart-pie" />
              </div>
            </div>
            <div style={{ marginLeft: 18 }}>
              <TextBlock size={10} weight={'bold'} color={Palette.GRAY} style={{ textAlign }}>
                LTARV
              </TextBlock>
              <TextBlock weight="bold" color={Palette.BLACK} style={{ textAlign }}>
                {highlights.ltarv}
              </TextBlock>
            </div>
          </div>
        );
      case 'loanAmount':
        return (
          <div style={{ alignItems: 'center', display: 'flex' }}>
            <div>
              <div
                style={{
                  alignItems: 'center',
                  backgroundColor: Palette.WHITE,
                  borderRadius: 25,
                  display: 'flex',
                  height: 40,
                  justifyContent: 'center',
                  width: 40,
                }}>
                <Icon color="black" boxSize={'20px'} icon="dollar-sign" />
              </div>
            </div>
            <div style={{ marginLeft: 18 }}>
              <TextBlock size={10} weight={'bold'} color={Palette.GRAY} style={{ textAlign }}>
                Loan Amount
              </TextBlock>
              <TextBlock weight="bold" color={Palette.BLACK} style={{ textAlign }}>
                {highlights.loanAmount}
              </TextBlock>
            </div>
          </div>
        );
      case 'structure':
        return (
          <div style={{ alignItems: 'center', display: 'flex' }}>
            <div>
              <div
                style={{
                  alignItems: 'center',
                  backgroundColor: Palette.WHITE,
                  borderRadius: 25,
                  display: 'flex',
                  height: 40,
                  justifyContent: 'center',
                  width: 40,
                }}>
                <Icon color="black" boxSize={'20px'} icon="sitemap" />
              </div>
            </div>
            <div style={{ marginLeft: 18 }}>
              <TextBlock size={10} weight={'bold'} color={Palette.GRAY} style={{ textAlign }}>
                Structure
              </TextBlock>
              <TextBlock weight="bold" color={Palette.BLACK} style={{ textAlign }}>
                {highlights.structure}
              </TextBlock>
            </div>
          </div>
        );
      case 'strategy':
        return (
          <div style={{ alignItems: 'center', display: 'flex' }}>
            <div>
              <div
                style={{
                  alignItems: 'center',
                  backgroundColor: Palette.WHITE,
                  borderRadius: 25,
                  display: 'flex',
                  height: 40,
                  justifyContent: 'center',
                  width: 40,
                }}>
                <Icon color="black" boxSize={'20px'} icon="chess-king-piece" />
              </div>
            </div>
            <div style={{ marginLeft: 18 }}>
              <TextBlock size={10} weight={'bold'} color={Palette.GRAY} style={{ textAlign }}>
                Strategy
              </TextBlock>
              <TextBlock weight="bold" color={Palette.BLACK} style={{ textAlign }}>
                {highlights.strategy}
              </TextBlock>
            </div>
          </div>
        );
      case 'totalCapitalization':
        return (
          <div style={{ alignItems: 'center', display: 'flex' }}>
            <div>
              <div
                style={{
                  alignItems: 'center',
                  backgroundColor: Palette.WHITE,
                  borderRadius: 25,
                  display: 'flex',
                  height: 40,
                  justifyContent: 'center',
                  width: 40,
                }}>
                <Icon color="black" boxSize={'20px'} icon="buildings" />
              </div>
            </div>
            <div style={{ marginLeft: 18 }}>
              <TextBlock size={10} weight={'bold'} color={Palette.GRAY} style={{ textAlign }}>
                Total Capitalization
              </TextBlock>
              <TextBlock weight="bold" color={Palette.BLACK} style={{ textAlign }}>
                {highlights.totalCapitalization}
              </TextBlock>
            </div>
          </div>
        );
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', width: DESKTOP_CONTENT_WIDTH }}>
      {keys.map((key, i) => {
        const contentAlignments = ['left', 'center', 'right'];
        const textAlign = i === keys.length ? 'right' : 'left';
        return (
          <div key={`highlight-${i}`} style={{ display: 'flex', flex: 1, justifyContent: contentAlignments[i] }}>
            {renderValue(key, textAlign)}
          </div>
        );
      })}
    </div>
  );
};
